import React, { useState, useEffect } from 'react'
import { Modal, Button, FormControl, FormGroup } from 'react-bootstrap'
import { useForm, Controller, SubmitHandler } from 'react-hook-form'
import ReactSelect, { Option } from 'react-select'

import Label from 'live/components/forms/ui/label'
import globalNotification from 'live/services/global-notification'
import i18n from 'live/services/i18n'

import { RoundedModal } from '../../../../../generic'
import { createDiagram } from '../../../../../services'
import { IOperation } from '../../../../../types'
import { TOP_EVENT_MAX_CHARS } from '../../../../../utils/constants'
import { IDiagramListItem } from '../../RiskDiagramList'

interface IProps {
    show: boolean
    handleClose: () => void
    handleSave: (id: number) => void
    item?: IDiagramListItem
    operations: IOperation[]
}

interface IFormData {
    hazard: string
    topEvent: string
    operation: Option<number>
}

const formDefault: IFormData = {
    hazard: '',
    operation: undefined,
    topEvent: ''
}

export const AddDiagramModal: React.FC<IProps> = ({ handleClose, handleSave, show, item, operations = [] }) => {
    const { handleSubmit, control, formState, setValue } = useForm<IFormData>({
        mode: 'all',
        defaultValues: formDefault
    })

    const [createLoading, setCreateLoading] = useState(false)
    const [manualValidation, setManualValidation] = useState(false)

    const handleCloseProtocol = (): void => {
        setValue('hazard', undefined)
        setValue('operation', undefined)
        setValue('topEvent', undefined)
        setCreateLoading(false)
        setManualValidation(false)
        handleClose()
    }

    const handleCreate: SubmitHandler<IFormData> = async (data): Promise<void> => {
        try {
            setCreateLoading(true)
            const postData = await createDiagram({
                hazard: {
                    name: data.hazard,
                    operation: { id: data.operation.value }
                },
                topEvent: { name: data.topEvent }
            })
            handleSave(postData.id)
            handleCloseProtocol()
        } catch (err) {
            globalNotification.error(err.message ?? i18n('An error occurred on create diagram.'))
        } finally {
            setCreateLoading(false)
        }
    }

    useEffect(() => {
        if (show && item) {
            setValue('hazard', item.hazard, { shouldValidate: true })
            setValue('topEvent', item.topEvent, { shouldValidate: true })
            setValue(
                'operation',
                operations.map(op => ({ value: op.id, label: op.description })).find(op => op.label === item.operation)
            )
            setManualValidation(false)
        } else {
            setManualValidation(true)
        }
    }, [show]) // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (formState.isValid) setManualValidation(false)
    }, [formState.isValid])

    return (
        <RoundedModal show={show} onHide={handleCloseProtocol} aria-labelledby="contained-modal-title">
            <form onSubmit={handleSubmit(handleCreate)}>
                <Modal.Header closeButton>
                    <Modal.Title id="contained-modal-title">{i18n('New Diagram')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Controller
                        rules={{ required: true }}
                        name="hazard"
                        control={control}
                        render={({ field, fieldState }) => (
                            <FormGroup validationState={fieldState.invalid && !field.value ? 'error' : 'success'}>
                                <Label required>{i18n('Hazard')}</Label>
                                <FormControl
                                    type="text"
                                    id="diagramList-createModal-field-hazard"
                                    ref={field.ref}
                                    onChange={field.onChange}
                                    onBlur={field.onBlur}
                                    value={field.value}
                                    maxLength={255}
                                    componentClass="textarea"
                                    style={{ height: 80, maxHeight: 80, minHeight: 80 }}
                                />
                            </FormGroup>
                        )}
                    />
                    <Controller
                        rules={{ required: true }}
                        name="topEvent"
                        control={control}
                        render={({ field, fieldState }) => (
                            <FormGroup validationState={fieldState.invalid && !field.value ? 'error' : 'success'}>
                                <Label required>{i18n('Top Event')}</Label>
                                <FormControl
                                    type="text"
                                    id="diagramList-createModal-field-topEvent"
                                    ref={field.ref}
                                    onChange={field.onChange}
                                    onBlur={field.onBlur}
                                    value={field.value}
                                    maxLength={TOP_EVENT_MAX_CHARS}
                                    componentClass="textarea"
                                    style={{ height: 80, maxHeight: 80, minHeight: 80 }}
                                />
                            </FormGroup>
                        )}
                    />
                    <Controller
                        rules={{ required: true }}
                        name="operation"
                        control={control}
                        render={({ field, fieldState }) => (
                            <FormGroup validationState={fieldState.invalid && !field.value ? 'error' : 'success'}>
                                <Label required>{i18n('Operation')}</Label>
                                <ReactSelect
                                    options={operations.map(op => ({ value: op.id, label: i18n(op.description) }))}
                                    id="diagramList-createModal-field-operation"
                                    onChange={(obj: Option<number>) => field.onChange(obj)}
                                    value={field.value}
                                    searchable={true}
                                    onBlur={field.onBlur}
                                />
                            </FormGroup>
                        )}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <div className="add-modal-footer-container">
                        <div className="add-modal-button-container">
                            <Button
                                className="no-outline-button"
                                bsStyle="primary"
                                type="submit"
                                id="diagramList-createModal-button-create"
                                disabled={manualValidation || !formState.isValid}
                            >
                                {createLoading ? (
                                    <>
                                        <i className="fa fa-spinner fa-spin" /> {i18n('Creating')}
                                    </>
                                ) : (
                                    i18n('Create')
                                )}
                            </Button>
                            <Button
                                className="no-outline-button"
                                type="button"
                                id="diagramList-createModal-button-close"
                                onClick={handleCloseProtocol}
                            >
                                {i18n('Close')}
                            </Button>
                        </div>
                        <div className="add-moddal-required-tag">{i18n('Required')} *</div>
                    </div>
                </Modal.Footer>
            </form>
        </RoundedModal>
    )
}
